import React, {useEffect, useState} from 'react'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {restRequest} from '../../../api-utils/apiQueries'
import {convertToHours} from '../../common-function/formatDateToYearMonth'
import {BiSolidUser} from 'react-icons/bi'
import {resolveMediaUrl} from '../../../api-utils/constantVariables'
import GiftCardCategoryBadges from './GiftCardCategoryBadges'
import {parseGiftCardData} from './giftCardHelpers'

const ProfileField = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => (
  <div className='row mb-7'>
    <label className='col-lg-4 fw-bold text-muted'>{label}</label>
    <div className='col-lg-8'>{children}</div>
  </div>
)

const GiftCardDetails = () => {
  const {id} = useParams()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [detail, setDetail] = useState<any>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchDetail = async () => {
      if (!id) return
      setIsLoading(true)
      setError('')
      try {
        const response: any = await restRequest.getGiftCardDetail(id)
        const payload = response?.data?.data
        if (payload) {
          setDetail(payload)
        } else {
          setError(response?.data?.message || 'Gift card redemption not found.')
          setDetail(null)
        }
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          'Unable to load gift card redemption details.'
        setError(msg)
        setDetail(null)
      } finally {
        setIsLoading(false)
      }
    }
    fetchDetail()
  }, [id])

  const parsed = parseGiftCardData(detail?.gift_card_data)
  const user = detail?.user
  const profileSrc = resolveMediaUrl(user?.profile_picture)

  return (
    <div>
      <div className='d-flex align-items-center gap-3 mb-5'>
        <button
          type='button'
          className='btn btn-sm btn-light-primary'
          onClick={() => navigate('/gift-card-management')}
        >
          ← Back to Gift Card Management
        </button>
      </div>

      <p className='card-label fw-bold fs-3 mb-5'>Gift Card Redemption Details</p>

      {isLoading ? (
        <div className='d-flex justify-content-center mt-10'>
          <div className='spinner-border text-primary' role='status' />
        </div>
      ) : error || !detail ? (
        <div className='alert alert-danger'>{error || 'Redemption not found.'}</div>
      ) : (
        <div className='card mb-5 mb-xl-10'>
          <div className='card-body pt-9 pb-0'>
            <div className='row g-10'>
              <div className='col-lg-5'>
                <h4 className='fw-bold text-gray-800 mb-6'>User</h4>
                <div className='d-flex flex-sm-nowrap mb-6'>
                  <div className='symbol symbol-80px symbol-lg-100px me-5'>
                    <div className='symbol-label bg-light rounded-circle d-flex align-items-center justify-content-center overflow-hidden'>
                      {profileSrc ? (
                        <img src={profileSrc} alt={user?.name ?? 'User'} className='w-100 h-100 object-fit-cover' />
                      ) : (
                        <BiSolidUser className='fs-1 text-gray-500' />
                      )}
                    </div>
                  </div>
                  <div>
                    {user?.id ? (
                      <Link
                        to={`/user-management/${user.id}`}
                        className='fs-2 fw-bold text-gray-800 text-hover-primary'
                      >
                        {user.name || '—'}
                      </Link>
                    ) : (
                      <span className='fs-2 fw-bold text-gray-800'>{user?.name ?? '—'}</span>
                    )}
                    <div className='text-muted fs-6 mt-1'>{user?.email || '—'}</div>
                  </div>
                </div>
              </div>

              <div className='col-lg-7'>
                <h4 className='fw-bold text-gray-800 mb-6'>Redemption</h4>
                <ProfileField label='Gift card'>{parsed.title}</ProfileField>
                <ProfileField label='Value'>
                  <span className='fw-semibold'>
                    {detail.gift_card_amount || parsed.amount}
                  </span>
                </ProfileField>
                <ProfileField label='Categories'>
                  <GiftCardCategoryBadges categories={parsed.categories} />
                </ProfileField>
                <ProfileField label='Receiver email'>
                  {detail.recipient_email || parsed.recipientEmail}
                </ProfileField>
                <ProfileField label='Product ID'>{parsed.productId}</ProfileField>
                <ProfileField label='Claimed at'>
                  {detail.createdAt
                    ? `${convertToHours(detail.createdAt)} (${new Date(detail.createdAt).toLocaleString()})`
                    : '—'}
                </ProfileField>
                {parsed.giftUrl ? (
                  <ProfileField label='Gift URL'>
                    <a href={parsed.giftUrl} target='_blank' rel='noreferrer' className='text-primary'>
                      Open gift card link
                    </a>
                  </ProfileField>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GiftCardDetails
